import React, { FC, useMemo, useState } from 'react';

import {
  Sorting,
  SortOrder,
  UserAction,
  UserActionRecordDetailed,
} from '@go-shared/types/userActionRecord';

import './UserRecords.scss';
import {
  BubbleButton,
  BubbleDrawer,
  BubbleOverlay,
  BubbleTable,
} from '@go-client/components/BubbleUI';
import { BubbleTableFields, BubbleTableTemplate } from '@go-client/components/BubbleUI/BubbleTable';
import { getLocaleDateTimeStringUTC, timeToAgoLocal } from '@go-shared/lib/time';
import { InlineIcon } from '@spark-labs/avocado-ui';
import { getActionClassname } from '@go-client/components/UserRecords/Visualizations/utils';
import { UserActionLog } from './UserActionLog';
import { UserActionSummary } from '@go-client/components/UserRecords/UserActionSummary';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { UserActionRecordUIFilters } from '@go-client/pages/UserActions/UsersActions';
import { getSortingIcon } from '@go-shared/lib/paginations';

type UserRecordsProps = {
  records: UserActionRecordDetailed[];
  loading: boolean;
  onFiltersChange: (filters: UserActionRecordUIFilters) => void;
  activeFilters: UserActionRecordUIFilters;
};

export const UserRecords: FC<UserRecordsProps> = ({
  records,
  loading,
  onFiltersChange,
  activeFilters,
}) => {
  const [timeToAgo, setTimeToAgo] = useState(false);
  const [modal, setModal] = useState<UserActionLog | undefined>(undefined);
  const [sortBy, setSortBy] = useState<Sorting>(
    activeFilters.sort ?? { createdAt: SortOrder.DESC }
  );

  const orderBy = (field: string) => {
    const [[actualField, actualDirection]] = Object.entries(sortBy);

    const direction = field === actualField ? actualDirection * -1 : SortOrder.DESC;

    const sort = { [field]: direction };

    setSortBy(sort);

    onFiltersChange({ ...activeFilters, sort });
  };

  const tableFields: Array<BubbleTableFields<UserActionRecordDetailed>> = [
    {
      key: 'userId',
      thProps: { onClick: () => orderBy('userId'), className: 'cursor-pointer whitespace-nowrap' },
      render: () => {
        const field = Object.keys(activeFilters.sort)[0];

        return (
          <>
            <span className="cursor-pointer">
              {'User'}
              {Object.keys(activeFilters.sort)[0] === 'userId' && (
                <FontAwesomeIcon
                  icon={getSortingIcon(sortBy[field])}
                  fixedWidth
                  size={'sm'}
                  className={'cursor-pointer'}
                />
              )}
            </span>
          </>
        );
      },
    },
    {
      key: 'createdAt',
      thProps: { onClick: () => orderBy('createdAt'), className: 'whitespace-nowrap' },
      render: () => {
        const field = Object.keys(activeFilters.sort)[0];

        return (
          <>
            <span className="cursor-pointer">
              {'Date'}
              {Object.keys(activeFilters.sort)[0] === 'createdAt' && (
                <FontAwesomeIcon
                  icon={getSortingIcon(sortBy[field])}
                  fixedWidth
                  size={'sm'}
                  className={'cursor-pointer'}
                />
              )}
            </span>
          </>
        );
      },
    },
    {
      key: 'action-url',
      label: 'Action & URL',
    },
    {
      key: 'details',
      label: 'Changes',
    },
  ];

  const tableItems = useMemo(() => {
    const result: UserActionRecordDetailed[] = records.map(record => {
      return {
        _id: record._id,
        createdAt: record.createdAt,
        action: record.action,
        user: record.user,
        url: record.url,
        body: record.body,
        method: record.method,
        urlParams: record.urlParams,
        summary: record.summary,
        query: record.query,
      };
    });

    return result;
  }, [records]);

  const tableTemplates: Array<BubbleTableTemplate<UserActionRecordDetailed>> = [
    {
      key: 'userId',
      render: ({ user }) => (
        <>
          <div className="block items-center">
            <InlineIcon name="user" className="text-grey w-5 h-5 mr-1" />
            <span>
              {user.firstName} {user.lastName}
            </span>
          </div>
        </>
      ),
    },
    {
      key: 'createdAt',
      render: ({ createdAt }) => (
        <>
          <div className="block whitespace-nowrap">
            <span className="time-span inline-block">
              {timeToAgo
                ? timeToAgoLocal(new Date(createdAt), new Date())
                : getLocaleDateTimeStringUTC(new Date(createdAt))}

              <button onClick={() => setTimeToAgo(!timeToAgo)} className="inline-block">
                <InlineIcon name="clock" className="text-grey w-5 h-5" />
              </button>
            </span>
          </div>
        </>
      ),
    },
    {
      key: 'action-url',
      render: record => <UserActionSummary {...record} />,
    },
    {
      key: 'details',
      render: (item: UserActionRecordDetailed) => (
        <div className="ml-auto mr-5 flex items-center">
          {item.body && (
            <BubbleButton
              className="p-2-i"
              variant="slate"
              inverse
              onClick={() => setModal(item)}
              icon="edit"
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="UserActionsBody mx-5 content-minus-logo min-w-900">
      <BubbleOverlay loading={loading}>
        <BubbleTable
          keyExtractor={e => e._id}
          fields={tableFields}
          items={tableItems}
          templates={tableTemplates}
          stickyHeader={130}
          headVariant="titanium"
          responsive
          striped
        />
      </BubbleOverlay>
      {modal && (
        <BubbleDrawer onClose={() => setModal(undefined)} containerClassName="bg-eggshell">
          <UserActionLog {...modal} onClose={() => setModal(undefined)} />
        </BubbleDrawer>
      )}
      <div className="mt-3">&nbsp;</div>
    </div>
  );
};

export const ActionChip: React.FC<{ action: UserAction }> = ({ action }) => {
  return (
    <span
      className={`rounded-full w-full uppercase px-5 py-2 text-xs font-bold mr-3 text-black ${getActionClassname(
        action
      )}`}
    >
      {action}
    </span>
  );
};
